import { useEffect, useRef, useState, type ElementType, type ReactElement } from 'react';
import { Fragment } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkRehype from 'remark-rehype';
import rehypeReact from 'rehype-react';
import rehypeShiki from '@shikijs/rehype';

export interface UseRemarkResult {
  content: ReactElement | null;
  error: Error | null;
}

export type RemarkComponents = Record<string, ElementType>;

/**
 * Async markdown → React pipeline with Shiki syntax highlighting.
 * Debounces updates during streaming to avoid re-processing on every chunk.
 */
export function useRemark(
  markdown: string,
  debounceMs = 100,
  components?: RemarkComponents,
  codeTheme = 'github-dark',
): UseRemarkResult {
  const [content, setContent] = useState<ReactElement | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const processorRef = useRef<ReturnType<typeof unified> | null>(null);

  useEffect(() => {
    processorRef.current = unified()
      .use(remarkParse)
      .use(remarkGfm)
      .use(remarkRehype, { allowDangerousHtml: false })
      .use(rehypeShiki, {
        themes: { dark: codeTheme },
        defaultColor: 'dark',
        langs: [
          'typescript',
          'javascript',
          'python',
          'bash',
          'json',
          'markdown',
          'css',
          'html',
          'yaml',
          'tsx',
          'jsx',
          'gdscript',
          'gdresource',
          'gdshader',
        ],
      })
      .use(rehypeReact, {
        Fragment,
        jsx,
        jsxs,
        components,
      });
  }, [components, codeTheme]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(async () => {
      if (!processorRef.current) {
        return;
      }

      try {
        const result = await processorRef.current.process(markdown);
        setContent(result.result as ReactElement);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
        // keep previous render on error to avoid flashing blank state
      }
    }, Math.max(0, debounceMs));

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [debounceMs, markdown, codeTheme]);

  return { content, error };
}
